
import { memo } from 'react';
import { type Editor } from '@tiptap/react';
import {
    Bold,
    Italic,
    Strikethrough,
    Code,
    FileCode,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Table,
    Undo,
    Redo,
    RemoveFormatting,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ToolbarButtonProps {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    shortcut?: string;
    children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, disabled, title, shortcut, children }: ToolbarButtonProps) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    className={cn(
                        'flex items-center justify-center w-8 h-8 rounded',
                        'transition-colors duration-150',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isActive
                            ? 'bg-amber-100 text-amber-700'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    )}
                >
                    {children}
                </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
                {title}
                {shortcut && (
                    <span className="ml-2 text-slate-400">{shortcut}</span>
                )}
            </TooltipContent>
        </Tooltip>
    );
}

function ToolbarDivider() {
    return <div className="w-px h-6 mx-1 bg-slate-200" />;
}

interface WysiwygToolbarProps {
    editor: Editor;
}

/**
 * WysiwygToolbar - WYSIWYG formatting toolbar for TipTap editor
 * 
 * Provides buttons for rich text formatting:
 * - Inline: bold, italic, strikethrough, code
 * - Block: headings, lists, quotes
 * - Utility: undo, redo, clear formatting
 */
export const WysiwygToolbar = memo(function WysiwygToolbar({ editor }: WysiwygToolbarProps) {
    return (
        <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50 flex-wrap">
                {/* Undo/Redo */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    title="Undo"
                    shortcut="⌘Z"
                >
                    <Undo size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    title="Redo"
                    shortcut="⌘⇧Z"
                >
                    <Redo size={16} />
                </ToolbarButton>

                <ToolbarDivider />

                {/* Text Formatting */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title="Bold"
                    shortcut="⌘B"
                >
                    <Bold size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title="Italic"
                    shortcut="⌘I"
                >
                    <Italic size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive('strike')}
                    title="Strikethrough"
                >
                    <Strikethrough size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    isActive={editor.isActive('code')}
                    title="Inline Code"
                    shortcut="⌘E"
                >
                    <Code size={16} />
                </ToolbarButton>

                <ToolbarDivider />

                {/* Headings */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive('heading', { level: 1 })}
                    title="Heading 1"
                >
                    <Heading1 size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive('heading', { level: 2 })}
                    title="Heading 2"
                >
                    <Heading2 size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    isActive={editor.isActive('heading', { level: 3 })}
                    title="Heading 3"
                >
                    <Heading3 size={16} />
                </ToolbarButton>

                <ToolbarDivider />

                {/* Lists & Quotes */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="Bullet List"
                >
                    <List size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="Numbered List"
                >
                    <ListOrdered size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive('blockquote')}
                    title="Quote"
                >
                    <Quote size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    isActive={editor.isActive('codeBlock')}
                    title="Code Block"
                >
                    <FileCode size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() =>
                        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                    }
                    title="Insert Table"
                >
                    <Table size={16} />
                </ToolbarButton>

                <ToolbarDivider />

                {/* Clear Formatting */}
                <ToolbarButton
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                    title="Clear Formatting"
                >
                    <RemoveFormatting size={16} />
                </ToolbarButton>
            </div>
        </TooltipProvider>
    );
});

